import type { AdobeDataLayerEvent, FormData, PageViewEventParams, SearchData } from '@/contexts/analytics/utils/types';

import { sanitizeValue, pushToAdobeDataLayer } from '@/contexts/analytics/utils/adobeDataLayer';
import { sha256 } from '@/contexts/analytics/utils/hashUtils';
import { getPageCategory, getScreenDetails } from '@/contexts/analytics/utils/screenMapping';

// Substring to identify companies to exclude from tracking (case-insensitive)
const EXCLUDE_FROM_TRACKING = 'test account';

const buildPageViewEvent = async (params: PageViewEventParams): Promise<AdobeDataLayerEvent> => {
  const event: AdobeDataLayerEvent = {
    event: 'pageView',
    _volvo: {
      universal: {
        generic: {
          brand: {
            name: 'Volvo Penta',
            type: 'Global',
            identifier: 'volvo-penta-connect',
            division: 'Marine',
            region: 'Global',
            market: 'Global',
          },
          user: {
            type: sanitizeValue(params.user.type),
            role: sanitizeValue(params.user.role),
            loginStatus: params.user.loginStatus,
            signUpMethod: sanitizeValue(params.user.signUpMethod),
          },
          company:
            params.fleet.companyId || params.fleet.companyName
              ? {
                  id: sanitizeValue(params.fleet.companyId),
                  name: sanitizeValue(params.fleet.companyName),
                  info: { fleetSize: sanitizeValue(params.fleet.size) },
                  affiliation: { primarySegment: params.fleet.primarySegment || 'Unknown' },
                }
              : undefined,
          fleet: params.fleet.id
            ? {
                id: sanitizeValue(params.fleet.id),
                size: sanitizeValue(params.fleet.size),
                sizeNum: params.fleet.sizeNum,
              }
            : undefined,
        },
        identity: {
          email: sanitizeValue(params.user.email),
          emailSHA256: params.user.email ? await sha256(params.user.email) : undefined,
          ecid: undefined,
        },
        web: {
          page: {
            category: getPageCategory(params.pathname),
            language: params.language,
            country: params.country,
          },
        },
      },
    },
    web: {
      webPageDetails: {
        URL: params.url.split('?')[0],
        isErrorPage: params.pathname.includes('/error') || params.pathname.includes('/404'),
        isHomePage: params.pathname === '/' || params.pathname === '/dashboard',
        name: params.title,
        server: typeof window !== 'undefined' ? window.location.host : '',
        siteSection: params.pathname,
        viewName: params.pathname.split('/').filter(Boolean).join('-') || 'home',
        pageViews: { value: 1 },
      },
    },
    application: {
      version: sanitizeValue(import.meta.env.VITE_APP_VERSION),
      name: 'Volvo Penta Connect',
    },
  };

  if (params.isMobileContext) {
    event._volvo.universal.mobile = {
      screenDetails: getScreenDetails(params.pathname),
    };
  }

  return event;
};

const buildUnitRegistrationEvent = (
  title: string,
  selectedOptions: { attribute: string; value: string }[],
  details: string
): AdobeDataLayerEvent => ({
  event: 'componentInteraction',
  _volvo: {
    universal: {
      web: {
        component: {
          parent: 'Units',
          name: 'Unit Registration',
          title: title,
          details: details,
          selectedOptions: selectedOptions,
          componentInteraction: {
            value: 1,
          },
        },
      },
    },
  },
  web: {
    webInteraction: {
      type: 'other',
    },
  },
});

const buildSearchEvent = (search: SearchData): AdobeDataLayerEvent => ({
  event: 'siteSearch',
  _volvo: {
    universal: {
      web: {
        search,
      },
    },
  },
  web: {
    webInteraction: {
      type: 'other',
    },
  },
});

const buildFormEvent = (
  eventName: 'formView' | 'formSubmitSuccess' | 'formSubmitError',
  form: FormData
): AdobeDataLayerEvent => ({
  event: eventName,
  _volvo: {
    universal: {
      web: {
        form,
      },
    },
  },
  web: {
    webInteraction: {
      type: 'other',
    },
  },
});

export const analyticsEvents = {
  PageView: {
    track: async (params: PageViewEventParams): Promise<void> => {
      try {
        if (!params.fleet.companyId && params.fleet.companyId?.toLowerCase().includes(EXCLUDE_FROM_TRACKING)) {
          console.debug('Page view event params:', params);
        } else {
          // console.info('Tracking page view:', params.pathname);
          const event = await buildPageViewEvent(params);
          pushToAdobeDataLayer(event);
        }
      } catch (error) {
        console.error('Error tracking page view:', error);
      }
    },
  },

  Form: {
    view: (props: { name: string; destination: string; id?: string }): void => {
      pushToAdobeDataLayer(
        buildFormEvent('formView', {
          type: 'registration',
          name: props.name,
          id: props.id,
          destination: props.destination,
          formView: { value: 1 },
        })
      );
    },

    submitSuccess: (props: { name: string; destination: string; id?: string; topic?: string }): void => {
      pushToAdobeDataLayer(
        buildFormEvent('formSubmitSuccess', {
          type: 'registration',
          name: props.name,
          id: props.id,
          destination: props.destination,
          topic: props.topic,
          formSubmitSuccess: { value: 1 },
        })
      );
    },

    submitError: (props: { name: string; destination: string; errorMessage: string; id?: string }): void => {
      pushToAdobeDataLayer(
        buildFormEvent('formSubmitError', {
          type: 'registration',
          name: props.name,
          id: props.id,
          destination: props.destination,
          errorMessage: props.errorMessage,
          formSubmitError: { value: 1 },
        })
      );
    },
  },

  Search: {
    siteSearch: (props: { term?: string; numberTotal?: number; numberPerPage?: number; location?: string }): void => {
      const event = buildSearchEvent({
        ...(props.term !== undefined && { term: props.term }),
        ...(props.numberTotal !== undefined && {
          results: {
            numberTotal: String(props.numberTotal),
            ...(props.numberPerPage !== undefined && { numberPerPage: String(props.numberPerPage) }),
          },
        }),
        siteSearch: { value: 1 },
        ...(props.location !== undefined && { location: props.location }), // components name
      });
      pushToAdobeDataLayer(event);
    },
  },

  UnitRegistration: {
    unitSearch: (props: {
      companyName: string;
      mainSegment: string;
      segment: string;
      segmentDetail: string;
      maximumReached: boolean;
      alreadyAdded: boolean;
      unitNotExistInProductCenter: boolean;
    }): void => {
      if (props.companyName.toLowerCase().includes(EXCLUDE_FROM_TRACKING)) {
        console.debug('Unit registration search event props:', props);
      } else {
        const event = buildUnitRegistrationEvent(
          'Find unit',
          [
            { attribute: 'mainSegment', value: props.mainSegment },
            { attribute: 'segment', value: props.segment },
            { attribute: 'segmentDetail', value: props.segmentDetail },
            { attribute: 'maximumReached', value: props.maximumReached ? 'true' : 'false' },
            { attribute: 'alreadyAdded', value: props.alreadyAdded ? 'true' : 'false' },
            { attribute: 'nonExisting', value: props.unitNotExistInProductCenter ? 'true' : 'false' },
          ],
          'Completed - Unit search'
        );
        pushToAdobeDataLayer(event);
      }
    },

    notComplete: (props: { companyName: string }): void => {
      if (props.companyName.toLowerCase().includes(EXCLUDE_FROM_TRACKING)) {
        console.debug('Unit registration not completed event props:', props);
      } else {
        const event = buildUnitRegistrationEvent(
          'Find unit',
          [{ attribute: 'message', value: 'User did not complete the unit registration process' }],
          'Not completed'
        );
        pushToAdobeDataLayer(event);
      }
    },

    contactInformation: (props: {
      companyName: string;
      unitNotExistInProductCenter: boolean;
      mainSegment: string;
      segment: string;
      segmentDetail: string;
      ownershipConfirmed: boolean;
      notificationPreferences: boolean;
    }): void => {
      if (props.companyName.toLowerCase().includes(EXCLUDE_FROM_TRACKING)) {
        console.debug('Unit registration contact information event props:', props);
      } else {
        const event = buildUnitRegistrationEvent(
          'Contact information',
          [
            { attribute: 'nonExisting', value: props.unitNotExistInProductCenter ? 'true' : 'false' },
            { attribute: 'mainSegment', value: props.mainSegment },
            { attribute: 'segment', value: props.segment },
            { attribute: 'segmentDetail', value: props.segmentDetail },
            { attribute: 'ownershipConfirmed', value: props.ownershipConfirmed ? 'true' : 'false' },
            { attribute: 'notificationPreferences', value: props.notificationPreferences ? 'true' : 'false' },
          ],
          'Completed - Contact information provided'
        );
        pushToAdobeDataLayer(event);
      }
    },

    unitInformation: (props: {
      companyName: string;
      unitNotExistInProductCenter: boolean;
      mainSegment: string;
      segment: string;
      segmentDetail: string;
      engineSerialNumbers: number;
      manufacturer: string;
      model: string;
      vesselSegment: string;
      typeOfVessel: string;
    }): void => {
      if (props.companyName.toLowerCase().includes(EXCLUDE_FROM_TRACKING)) {
        console.debug('Unit registration unit information event props:', props);
      } else {
        const event = buildUnitRegistrationEvent(
          'Unit information',
          [
            { attribute: 'nonExisting', value: props.unitNotExistInProductCenter ? 'true' : 'false' },
            { attribute: 'mainSegment', value: props.mainSegment },
            { attribute: 'segment', value: props.segment },
            { attribute: 'segmentDetail', value: props.segmentDetail },
            { attribute: 'engineSerialNumbers', value: String(props.engineSerialNumbers) },
            { attribute: 'manufacturer', value: props.manufacturer },
            { attribute: 'model', value: props.model },
            { attribute: 'vesselSegment', value: props.vesselSegment },
            { attribute: 'typeOfVessel', value: props.typeOfVessel },
          ],
          'Completed - Unit information provided'
        );
        pushToAdobeDataLayer(event);
      }
    },

    success: (props: {
      companyName: string;
      unitNotExistInProductCenter: boolean;
      mainSegment: string;
      segment: string;
      segmentDetail: string;
    }): void => {
      if (props.companyName.toLowerCase().includes(EXCLUDE_FROM_TRACKING)) {
        console.debug('Unit registration success event props:', props);
      } else {
        const event = buildUnitRegistrationEvent(
          'Unit added',
          [
            { attribute: 'nonExisting', value: props.unitNotExistInProductCenter ? 'true' : 'false' },
            { attribute: 'mainSegment', value: props.mainSegment },
            { attribute: 'segment', value: props.segment },
            { attribute: 'segmentDetail', value: props.segmentDetail },
          ],
          'Completed - Unit successfully added'
        );
        pushToAdobeDataLayer(event);
      }
    },
  },

  Interaction: {
    componentInteraction: (props: {
      parent: string;
      title: string;
      type: string;
      href?: string;
      placement?: string;
      selectedOptions?: { attribute: string; value: string }[];
    }): void => {
      const event: AdobeDataLayerEvent = {
        event: 'componentInteraction',
        _volvo: {
          universal: {
            web: {
              component: {
                parent: props.parent,
                title: props.title,
                type: props.type,
                ...(props.href && { href: props.href }),
                ...(props.placement && { placement: props.placement }),
                ...(props.selectedOptions && { selectedOptions: props.selectedOptions }),
                componentInteraction: { value: 1 },
              },
            },
          },
        },
        web: {
          webInteraction: {
            type: 'other',
          },
        },
      };
      pushToAdobeDataLayer(event);
    },
  },
};
